import { gql } from "@apollo/client";
import { useEffect, useRef, useState } from "react";
import { SearchSelect } from "./SearchSelect";
import client from "../../../graphqlClient";

const GET_SHOPS = gql`
  query ShopSearchSelect_GetShops {
    shops {
      id
      shopType
      shopKey
      title
    }
  }
`;

export type ShopSearchSelect_ShopOption = {
  id: string;
  shopType: string;
  shopKey: string;
  title: string;
};

type ShopSearchSelect_GetShopsQuery = {
  shops: ShopSearchSelect_ShopOption[];
};

function getShopLabel(shop: ShopSearchSelect_ShopOption): string {
  return `${shop.title} (${shop.shopType})`;
}

export default function Shop_SearchSelect({
  selectedShop,
  onShopChange,
}: {
  selectedShop: ShopSearchSelect_ShopOption | null;
  onShopChange: (shop: ShopSearchSelect_ShopOption | null) => void;
}) {
  const shopsCacheRef = useRef<ShopSearchSelect_ShopOption[] | null>(null);
  const [resolvedSelectedShop, setResolvedSelectedShop] = useState<ShopSearchSelect_ShopOption | null>(selectedShop);

  const loadShops = async () => {
    if (shopsCacheRef.current) {
      return shopsCacheRef.current;
    }

    const result = await client.query<ShopSearchSelect_GetShopsQuery>({
      query: GET_SHOPS,
      fetchPolicy: 'network-only',
    });

    if (!result.data) {
      throw result.errors;
    }

    shopsCacheRef.current = result.data.shops;
    return result.data.shops;
  };

  const loadOptions = async (searchText: string) => {
    const shops = await loadShops();
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return shops;
    }

    return shops.filter((shop) => {
      const label = getShopLabel(shop).toLowerCase();
      return label.includes(normalizedSearch)
        || shop.shopKey.toLowerCase().includes(normalizedSearch);
    });
  };

  useEffect(() => {
    if (!selectedShop) {
      setResolvedSelectedShop(null);
      return;
    }

    if (selectedShop.title && selectedShop.shopType) {
      setResolvedSelectedShop(selectedShop);
      return;
    }

    loadShops()
      .then((shops) => {
        const match = shops.find((shop) => shop.id === selectedShop.id) ?? selectedShop;
        setResolvedSelectedShop(match);
      })
      .catch((error) => {
        console.error('Error loading selected shop:', error);
        setResolvedSelectedShop(selectedShop);
      });
  }, [selectedShop]);

  return (
    <SearchSelect<ShopSearchSelect_ShopOption>
      loadOptions={loadOptions}
      value={resolvedSelectedShop}
      onSelect={(option) => onShopChange(option)}
      onClear={() => onShopChange(null)}
      renderOption={(option, selectCallback) => (
        <div key={option.id} className="px-2 py-1" onClick={selectCallback}>
          {getShopLabel(option)}
        </div>
      )}
      renderSelected={(option) => getShopLabel(option)}
      label="Shop"
      placeholder="Select a shop..."
    />
  );
}
